import { Component, ErrorInfo, ReactNode } from 'react';
import ErrorFallback from '../ErrorFallback/ErrorFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global Error Boundary to catch rendering errors in the component tree.
 * When a crash occurs, it displays the branded ErrorFallback UI.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null 
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { 
      hasError: true, 
      error 
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can log the error to an external service here (e.g., Sentry)
    console.error('[ErrorBoundary] Caught crash:', error, errorInfo);
  }

  resetError = () => {
    this.setState({ 
      hasError: false, 
      error: null 
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <ErrorFallback 
          error={this.state.error || undefined} 
          resetErrorBoundary={this.resetError} 
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
